import { useMutation, useQueryClient } from "@tanstack/vue-query";

import { toastKiliError } from "@/data/api/kili/common/toast-kili-error";
import { InternalMutationKeys } from "@/data/api/kili/internal/internal-query-keys";
import kiliApi from "@/data/api/kili/kili-api";
import { IPlanningOverridesByApplianceCategoryKeyParams, SitesQueryKeys } from "@/data/api/kili/sites/sites-query-keys";
import {
  TApplyPlanningOverride,
  TPlanningOverrideBySection,
  TPlanningOverrideHeaterRange,
  TSectionPlanning,
} from "@/data/api/kili/sites/types/planning.type";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import { ECommonProgramMetaMode } from "@/domains/v4b/program/subprogram.types";

export interface IApplyPlanningOverridesBySectionCommand {
  sectionRanges: TApplyPlanningOverride["sectionRanges"];
  siteId: number;
  applianceCategory: EProgramApplianceCategory;
}

export function useApplyPlanningOverridesBySection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: InternalMutationKeys.applyPlanningOverrides(),
    mutationFn: async (opts: IApplyPlanningOverridesBySectionCommand) => {
      return await kiliApi.applyPlanningOverridesBySection(
        {
          sectionRanges: opts.sectionRanges,
        },
        {
          params: {
            siteId: opts.siteId,
            applianceCategory: opts.applianceCategory,
          },
        },
      );
    },
    onMutate: (opts: IApplyPlanningOverridesBySectionCommand) => {
      const params: IPlanningOverridesByApplianceCategoryKeyParams = {
        siteId: opts.siteId,
        applianceCategory: opts.applianceCategory,
      };

      const previousData = queryClient.getQueryData<TPlanningOverrideBySection>(
        SitesQueryKeys.planningOverridesByApplianceCategory(params),
      );

      if (previousData) {
        queryClient.setQueryData<TPlanningOverrideBySection>(
          SitesQueryKeys.planningOverridesByApplianceCategory(params),
          (previous) => {
            if (!previous) {
              return;
            }

            return {
              applianceCategory: previous.applianceCategory,
              sections: previous.sections.map((planning) => {
                const maybeEditedSectionPlanningOverrides = opts.sectionRanges.find(
                  (section) => section.sectionId === planning.sectionId,
                );

                if (!maybeEditedSectionPlanningOverrides) {
                  return planning;
                }

                return {
                  ...planning,
                  ranges: (maybeEditedSectionPlanningOverrides.ranges ?? []).map((schedule) => {
                    if (schedule.metaMode === ECommonProgramMetaMode.TEMP) {
                      return {
                        endTime: new Date(schedule.endTime),
                        startTime: new Date(schedule.startTime),
                        metaMode: ECommonProgramMetaMode.TEMP,
                        temperature: schedule.temperature!,
                      };
                    }

                    return {
                      endTime: new Date(schedule.endTime),
                      startTime: new Date(schedule.startTime),
                      metaMode: schedule.metaMode,
                    } as TPlanningOverrideHeaterRange;
                  }),
                } satisfies TSectionPlanning;
              }),
            };
          },
        );
      }

      return {
        previousData,
      };
    },
    onError: (err, params, context) => {
      toastKiliError(err, "wizard.piloting.messages.apply.error");

      if (!context) {
        return;
      }

      // If something went wrong, we want to rollback to our previous cached data
      queryClient.setQueryData<TPlanningOverrideBySection>(
        SitesQueryKeys.planningOverridesByApplianceCategory(params),
        context.previousData,
      );
    },
    onSettled: async (_, __, params) => {
      await queryClient.removeQueries({
        queryKey: SitesQueryKeys.planningOverridesByApplianceCategory(params),
      });
    },
  });
}
